import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { CloudflareDevice } from "../shared/types";
import {
  adminQueries,
  adminQueryKeys,
  optimisticallyRevokeCloudflareDevice,
  restoreCloudflareDevicePages,
  useAdminApi,
  type CloudflareDeviceQueryFilters,
} from "./admin-query";
import { CapabilityBadge, EmptyState } from "./AdminUi";
import { errorMessage } from "./api";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { Icon } from "./Icon";

type DeviceStatus = NonNullable<CloudflareDeviceQueryFilters["status"]>;

function formatCloudflareDate(value?: string): string {
  if (!value) return "Never";
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "Unknown";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

function deviceFilters(
  search: string,
  status: DeviceStatus,
): CloudflareDeviceQueryFilters {
  return {
    ...(search ? { search } : {}),
    ...(status === "all" ? {} : { status }),
  };
}

export function CloudflareDevicesSection() {
  const queryClient = useQueryClient();
  const adminApi = useAdminApi();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<DeviceStatus>("all");
  const [pending, setPending] = useState<CloudflareDevice>();
  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(query.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [query]);
  const devicesQuery = useInfiniteQuery(
    adminQueries.cloudflareDevices(25, deviceFilters(search, status)),
  );
  const postureQuery = useQuery(adminQueries.cloudflareDevicePosture());
  const pages = devicesQuery.data?.pages ?? [];
  const devices = [
    ...new Map(
      pages
        .flatMap((page) => page.devices)
        .map((device) => [device.id, device]),
    ).values(),
  ];
  const capability = pages[0]?.capability ?? "available";
  const total = pages[0]?.total;
  const postureRules = postureQuery.data?.rules ?? [];
  const enabledPostureRules = postureRules.filter((rule) => rule.enabled);

  const revokeMutation = useMutation({
    mutationFn: async (device: CloudflareDevice) => {
      await adminApi(
        `/api/admin/cloudflare/devices/${encodeURIComponent(device.id)}/revoke`,
        {
          method: "POST",
          body: JSON.stringify({ confirmation: device.id }),
        },
      );
    },
    onMutate: async (device) => {
      await queryClient.cancelQueries({
        queryKey: adminQueryKeys.cloudflareDevices,
      });
      return {
        snapshots: optimisticallyRevokeCloudflareDevice(queryClient, device.id),
      };
    },
    onError: (_caught, _device, context) => {
      if (context?.snapshots) {
        restoreCloudflareDevicePages(queryClient, context.snapshots);
      }
    },
    onSuccess: async () => {
      setPending(undefined);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: adminQueryKeys.cloudflareDevices,
        }),
        queryClient.invalidateQueries({
          queryKey: adminQueryKeys.cloudflare,
        }),
        queryClient.invalidateQueries({ queryKey: adminQueryKeys.security }),
      ]);
    },
  });

  const emptyTitle =
    capability === "not_configured"
      ? "Cloudflare devices are not configured"
      : search || status !== "all"
        ? "No devices match these filters"
        : "No WARP devices found";
  const emptyHint =
    capability === "not_configured"
      ? "Add the Cloudflare account API bindings to load device trust."
      : "Devices appear after they enroll with the Cloudflare One Client.";

  return (
    <section className="directory-card cloudflare-section device-trust-section">
      <header className="collection-header">
        <div>
          <h2>Device trust</h2>
          <p>
            WARP enrollment, ownership, profile coverage, and posture policy.
          </p>
        </div>
        <div className="collection-header__actions">
          <CapabilityBadge capability={capability} />
          <span className="collection-header__count">
            {total ?? devices.length}
          </span>
        </div>
      </header>

      <div
        className="device-posture-summary"
        aria-label="Device posture checks"
      >
        <div>
          <strong>{enabledPostureRules.length}</strong>
          <span>enabled posture checks</span>
        </div>
        {postureQuery.isError ? (
          <small>Posture checks could not be loaded.</small>
        ) : postureRules.length ? (
          <ul>
            {postureRules.slice(0, 6).map((rule) => (
              <li key={rule.id}>
                <span
                  className={`status ${rule.enabled ? "status--active" : "status--disabled"}`}
                >
                  <i />
                  {rule.name}
                </span>
                <small>
                  {rule.type.replaceAll("_", " ")}
                  {rule.platforms.length
                    ? ` · ${rule.platforms.join(", ")}`
                    : ""}
                  {rule.schedule ? ` · every ${rule.schedule}` : ""}
                </small>
              </li>
            ))}
          </ul>
        ) : postureQuery.data ? (
          <small>No device posture checks are configured.</small>
        ) : (
          <small>Loading posture checks…</small>
        )}
        {postureRules.length > 6 && (
          <small>+{postureRules.length - 6} more checks</small>
        )}
      </div>

      <div className="directory-toolbar">
        <label className="search-field">
          <span className="sr-only">Search devices or users</span>
          <Icon name="search" size={17} />
          <input
            aria-label="Search devices or users"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search devices or users"
            type="search"
            value={query}
          />
        </label>
        <label className="select-field select-field--compact">
          <span className="sr-only">Filter device registrations</span>
          <select
            aria-label="Filter device registrations"
            onChange={(event) => setStatus(event.target.value as DeviceStatus)}
            value={status}
          >
            <option value="all">All registrations</option>
            <option value="active">Active registrations</option>
            <option value="revoked">Revoked registrations</option>
          </select>
        </label>
      </div>

      {devicesQuery.isError && !devicesQuery.data && (
        <div className="notice notice--error device-trust-notice" role="alert">
          {errorMessage(
            devicesQuery.error,
            "Cloudflare devices could not be loaded.",
          )}
        </div>
      )}
      {devicesQuery.isError && devicesQuery.data && (
        <div
          className="notice notice--warning device-trust-notice"
          role="status"
        >
          Could not refresh — showing the last loaded devices.
        </div>
      )}

      <div className="table-wrap">
        <table className="cloudflare-devices-table">
          <caption className="sr-only">Cloudflare WARP devices</caption>
          <thead>
            <tr>
              <th>Device</th>
              <th>Last user</th>
              <th>Platform</th>
              <th>Device profile</th>
              <th>Last seen</th>
              <th>Registration</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <tr key={device.id}>
                <td data-label="Device">
                  <strong>{device.name}</strong>
                  <small>{device.id}</small>
                </td>
                <td data-label="Last user">
                  <strong>{device.lastSeenUser?.name ?? "Unassigned"}</strong>
                  <small>
                    {device.lastSeenUser?.email ?? "No user recorded"}
                  </small>
                </td>
                <td data-label="Platform">
                  <strong>{device.deviceType}</strong>
                  <small>
                    {device.osVersion ? `OS ${device.osVersion}` : "OS unknown"}
                    {device.clientVersion
                      ? ` · WARP ${device.clientVersion}`
                      : ""}
                  </small>
                </td>
                <td data-label="Device profile">
                  <strong>
                    {device.profile?.name ?? "No profile returned"}
                  </strong>
                  {device.profile?.deleted && <small>Profile deleted</small>}
                </td>
                <td data-label="Last seen">
                  {formatCloudflareDate(device.lastSeenAt)}
                </td>
                <td data-label="Registration">
                  <span
                    className={`status ${device.activeRegistrations ? "status--active" : "status--disabled"}`}
                  >
                    <i />
                    {device.activeRegistrations
                      ? `${device.activeRegistrations} active`
                      : "Revoked"}
                  </span>
                </td>
                <td data-label="Actions">
                  <button
                    className="button button--secondary button--compact"
                    disabled={!device.activeRegistrations}
                    onClick={() => {
                      revokeMutation.reset();
                      setPending(device);
                    }}
                    type="button"
                  >
                    Revoke device
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!devices.length &&
          !devicesQuery.isPending &&
          !devicesQuery.isError && (
            <EmptyState
              hint={emptyHint}
              onClearFilters={
                search || status !== "all"
                  ? () => {
                      setQuery("");
                      setSearch("");
                      setStatus("all");
                    }
                  : undefined
              }
              title={emptyTitle}
            />
          )}
      </div>

      <footer className="directory-footer pagination-footer">
        <span>
          Showing {devices.length}
          {typeof total === "number" ? ` of ${total}` : ""} devices
        </span>
        {devicesQuery.hasNextPage && (
          <button
            className="button button--quiet button--compact"
            disabled={devicesQuery.isFetchingNextPage}
            onClick={() => void devicesQuery.fetchNextPage()}
            type="button"
          >
            {devicesQuery.isFetchingNextPage
              ? "Loading devices…"
              : "Load more devices"}
          </button>
        )}
      </footer>

      {pending && (
        <ConfirmationDialog
          busy={revokeMutation.isPending}
          busyLabel="Revoking device…"
          confirmLabel="Revoke device"
          description="Every WARP registration for this physical device will be revoked. The user must enroll it again before it can satisfy device-based policies."
          error={
            revokeMutation.error
              ? errorMessage(
                  revokeMutation.error,
                  "The device could not be revoked.",
                )
              : ""
          }
          icon="logout"
          onCancel={() => !revokeMutation.isPending && setPending(undefined)}
          onConfirm={() => revokeMutation.mutate(pending)}
          title={`Revoke ${pending.name}?`}
        />
      )}
    </section>
  );
}
